import { getEntriesByType, getEntriesByDate, getAllEntries } from "./entries"
// @ts-ignore
import { REFLECTION_OBJECT_KEYS, ENTRY } from "../apollo/fields"
import { UPDATE_ENTRY } from "../apollo/mutations"
import { safeReadQuery } from "../refactored-functions/helpers/graphql"
import { CURRENT_ENTRY, MONTHLY_REVIEW, YEARLY_REVIEW } from "../apollo/queries"
import {
  CURRENT_ENTRY_DATA,
  LOWLIGHT_REFLECTION_OBJECT,
  YEARLY_REVIEW_DATA,
  MONTHLY_REVIEW_DATA,
} from "../apollo/data"
// @ts-ignore
import client from "../apollo/client"
import { gql } from "@apollo/client"
import { errorer } from "../refactored-components/Dialogs/modals"
// @ts-ignore
import { navigate } from "gatsby"
import cache, { diffCheckCacheWrite } from "../apollo/cache"
import network from "../apollo/network"
import { setApolloState } from "../refactored-functions/state"
import { clone, cloneDeep } from "lodash"
import { USER_ENTRY_UPDATE } from "../apollo/fragments"
import moment from "moment"
import {
  getNextDay,
  dateLastDayOfMonth,
} from "../refactored-functions/helpers/dates"
import {
  setMonthlyReviewStarted,
  setYearlyReviewStarted,
} from "../utils/user-data"
import {
  initMonthlyReviewSubscriptions,
  MONTHLY_REVIEW_SUBSCRIPTION,
} from "../apollo/subscriptions/monthly-review"
import { updateSelectedUpgrade } from "./upgrade"

/**
 * @description Hello **world!**
 *
 * @typedef {{
 *  date: string
 *  image: string
 *  key: string
 *  month: string
 *  pinned: string
 *  platform: string
 *  reflection: string
 *  review: string
 *  tags: string
 *  text: string
 *  thumbnailImage: string
 *  type: string
 *  year: string
 * }}
 * UserEntry
 *
 * @typedef {{
 *  entryKey: string
 *  question?: string
 *  questionResponse?: string
 *  note?: string
 *  id?: string
 *  updatedAt?: number
 *  __typename?: string
 * }}
 * ReflectionObject
 *
 * @typedef {typeof MONTHLY_REVIEW_DATA|typeof YEARLY_REVIEW_DATA}
 * ReviewDataObject
 */

/**
 * Throws an error for invalid data.
 *
 * @param {object} reviewData the review object's data
 * @param {string} label      optional string to provide label in error
 */
function _throwInvalidReviewData(reviewData, label = "unlabeled") {
  console.log(`LOG: [${label}] -> reviewData (invalid)`, reviewData)
  throw Error(`Invalid review data provided to \`${label}\``)
}

/**
 * Determines the type of the review based on it's data
 * @param {ReviewDataObject} reviewData   the review object's data
 */
function _deriveReviewType(reviewData) {
  const type =
    // @ts-ignore
    reviewData && reviewData.__typename === "YearlyReview"
      ? "yearly"
      : // @ts-ignore
      reviewData && reviewData.__typename === "MonthlyReview"
      ? "monthly"
      : null
  return type
}

function _reflectionsFromReviewData(reviewData) {
  const type = _deriveReviewType(reviewData)
  switch (type) {
    case "yearly":
    case "monthly":
      return reviewData.lowlightsNotes.filter(({ entryKey }) => !!entryKey)
    default:
      return []
  }
}

/**
 * Extract all parent ID's of the reflection objects in the provided review
 * data into an array.
 *
 * @param {ReviewDataObject} reviewData   review data
 * @return {string[]}                     array of entry ID's
 */
function _getReflectionObjectParentIds(reviewData) {
  return _reflectionsFromReviewData(reviewData).map(({ entryKey }) => entryKey)
}

/**
 * Gets an array of reflection objects from the `reflection` field of all entries
 * associated with the review requested via it's config options.
 *
 * @param {{
 *  month?: string
 *  year: string|number
 * }} reviewConfig                    the review month and/or year (depending on type)
 * @param {"lowlight"} reflectionType the type of reflection
 * @returns {UserEntry[]}             an array of reflection objects
 */
function _getReflectionsFromEntries(reviewConfig, reflectionType = "lowlight") {
  const { month, year } = reviewConfig
  if (month && year)
    return getEntriesByType(getEntriesByDate(getAllEntries())(month, year))(
      reflectionType
    ).reverse()
  else if (!month && year)
    return getEntriesByType(getEntriesByDate(getAllEntries())(year))(
      reflectionType
    ).reverse()
  else return []
}

/**
 * Generates an array of new reflection objects from the provided parent entries.
 *
 * @param {UserEntry[]} entries array of parent entries
 */
function _createMissingReflectionObjects(entries) {
  return entries.map(({ key, reflection }) => {
    try {
      if (reflection && reflection !== "{}") return JSON.parse(reflection)
    } catch (err) {
      console.warn(
        `LOG: [_createMissingReflectionObjects] error while parsing entry reflection field`
      )
      console.log({ entries })
      console.error(err)
    }
    return newReflectionObject({ entryKey: key })
  })
}

/**
 * Merge an array of reflection objects on to the review's data object.
 *
 * @param {ReflectionObject[]} reflectionObjects array of reflection objects
 * @param {ReviewDataObject} reviewData           review data object
 * @param {"lowlight"} objectType                 type of reflection objects
 */
function _mergeReflectionObjectsToReview(
  reflectionObjects,
  reviewData,
  // @ts-ignore
  objectType = "lowlight"
) {
  const merged = {
    ...reviewData,
    ...{ lowlightsNotes: [...reviewData.lowlightsNotes, ...reflectionObjects] },
  }
  return merged
}

/**
 * Checks review data reflection objects for any that reference entries that no
 * longer exist (deleted by the user) and removes those items from the review
 * data.
 *
 * @see setReviewData                   when loading review data into cache
 * @param {ReviewDataObject} reviewData yearly or monthly review data
 */
function _removeDeletedReflectionObjects(reviewData) {
  let lowlightsNotes = reviewData?.lowlightsNotes || []

  const entries = getAllEntries(false)
  lowlightsNotes = lowlightsNotes
    .filter(
      reflection =>
        entries.filter(entry => entry.key === reflection.entryKey).length > 0
    )
    .filter(
      reflection =>
        entries.find(entry => entry.key === reflection.entryKey)?.pinned ===
        true
    )

  reviewData.lowlightsNotes = lowlightsNotes
  return reviewData
}

export function isReviewDataSupported(reviewData) {
  let supported = true
  if (!reviewData) return { supported: false, issue: "invalid review data" }
  const { lowlightsNotes } = reviewData
  if (!Array.isArray(lowlightsNotes))
    return { supported: false, issue: "lowlight reflections is not an array" }
  const unsupportedLowlightForm = lowlightsNotes.some(
    reflection => !reflection.entryKey
  )
  if (unsupportedLowlightForm)
    return {
      supported: false,
      issue:
        "review contains unsupported lowlight reflections without entry key references",
    }
  return { supported }
}

export function deriveSingleReflectionValue(key) {
  const currentReviewRendered = window.location.pathname.includes(
    "/compose/monthly"
  )
    ? "monthly"
    : window.location.pathname.includes("/compose/yearly")
    ? "yearly"
    : false
  let value = null,
    reviewData
  if (!currentReviewRendered) {
    // console.warn(
    //   "Invalid Route for request to `deriveSingleReflectionValue` neither a monthly or yearly review route present"
    // )
    debugger //_
    return null
  }
  switch (currentReviewRendered) {
    case "monthly":
      reviewData = cache.readQuery({ query: MONTHLY_REVIEW })?.monthlyReview
      break
    case "yearly":
      reviewData = cache.readQuery({ query: YEARLY_REVIEW })?.yearlyReview
      break
  }

  const parentEntry = cache.readPartialDocument({
    id: key,
    fragment: gql`
      fragment DeriveSingleReflectionValue on UserEntry {
        reflection
      }
    `,
  })

  if (!parentEntry) return null

  const currentReflectionField = parentEntry?.reflection

  if (currentReflectionField && currentReflectionField !== "{}") {
    try {
      const parsedCurrentReflectionField = JSON.parse(currentReflectionField)
      if (
        parsedCurrentReflectionField &&
        isValidReflectionObject(parsedCurrentReflectionField)
      ) {
        console.log(
          "LOG: [deriveSingleReflectionValue] found reflection field value",
          { key, data: parsedCurrentReflectionField }
        )
        return parsedCurrentReflectionField
      }
    } catch (err) {
      console.warn(
        "LOG: [deriveSingleReflectionValue] found value but failed to parse it"
      )
    }
  }

  if (reviewData?.id) {
    const { lowlightsNotes } = reviewData
    const matchingReflection = lowlightsNotes.find(obj => obj.entryKey === key)
    if (matchingReflection) {
      console.log(
        "LOG: [deriveSingleReflectionValue] found review data value",
        { key, data: matchingReflection }
      )
      return matchingReflection
    }
  }

  console.warn(
    "LOG: [deriveSingleReflectionValue] found no value for this key",
    key
  )
  return null
}

/**
 * Should run before any time $reviewData is set to global store.  Operates on
 * data and sets the proper lowlight reflection data object based on the available
 * data.  If a reflection object's parent entry has a reflection field with a saved
 * reflection, then return that instead of the reflection in the review's data.
 */
export function beforeSetReviewData(reviewData) {
  const final = cloneDeep(reviewData)
  final.lowlightsNotes = final.lowlightsNotes.map(reflection => {
    return deriveSingleReflectionValue(reflection.entryKey) || reflection
  })
  // Remove all reflection objects that do not have an existing parent entry
  final.lowlightsNotes = final.lowlightsNotes.filter(
    reflection => reflection !== null
  )
  return final
}

export function deriveProperReflectionObjectValues(reviewData) {
  /** Get the lowlightsNotes array from the review data */
  const lowlightsNotes =
    reviewData.lowlightsNotes.length > 0 ? reviewData.lowlightsNotes : []

  const reflectionsWithValues = lowlightsNotes.map((reflection, index) => {
    const { entryKey } = reflection
    if (!entryKey) return reflection
    const { reflection: currentReflectionField } = cache.readPartialDocument({
      id: entryKey,
      fragment: gql`
        fragment DeriveReflectionValue on UserEntry {
          reflection
        }
      `,
    })

    if (currentReflectionField && currentReflectionField !== "{}") {
      const parsedCurrentReflectionField = JSON.parse(currentReflectionField)
      if (!isValidReflectionObject(parsedCurrentReflectionField)) {
        return reflection
      } else {
        return parsedCurrentReflectionField
      }
    }
    return reflection
  })
  const updatedReviewData = cloneDeep(reviewData)
  updatedReviewData.lowlightsNotes = reflectionsWithValues
  const hasDiff = diffCheckCacheWrite(reviewData, updatedReviewData)
  return {
    reflections: reflectionsWithValues,
    reviewData: updatedReviewData,
    hasDiff,
  }
}

/**
 * Checks each pinned lowlight, and if a matching reflection object is not
 * found in the provided review data, and it has a saved reflection,
 * it generates one and adds it on to the review data.
 *
 * In certain edge cases, a review data's may be missing lowlight
 * reflections, for example, if a lowlight is created after the review
 * is completed, and then later the user edits the review.  The review's
 * saved JSON data would not include that later created lowlight.  Before
 * loading review data into the cache, this function appends any missing
 * reflections.
 *
 * @see   `setReviewData`                 when loading monthly review data into cache
 * @see   `currentEntry`                  subscription in monthly and yearly review contexts
 * @param {ReviewDataObject} reviewData   yearlyReview data
 */
export const appendMissingReflectionObjects = reviewData => {
  if (!reviewData.id) return reviewData
  let missingReflections = []

  /** Build an array of all lowlight reflection parent ID's */
  let parentIds = _getReflectionObjectParentIds(reviewData)

  /** Build an array of pinned lowlights for the year that have saved reflections */
  const missingLowlights = _getReflectionsFromEntries({
    // @ts-ignore
    month: reviewData.month,
    year: reviewData.year,
  }).filter(({ key, pinned }) => pinned && !parentIds.includes(key))

  /** Build an array of missing reflection objects from missing parent entries */
  missingReflections = _createMissingReflectionObjects(missingLowlights)

  /** Merge the missing reflection objects on to the review data */
  reviewData = _mergeReflectionObjectsToReview(
    missingReflections,
    reviewData,
    "lowlight"
  )

  reviewData = _removeDeletedReflectionObjects(reviewData)

  // const derivedValueReflections = deriveProperReflectionObjectValues(reviewData)
  // console.log("LOG: [deriveProperReflectionValues]", derivedValueReflections)

  // debugger
  return reviewData
}

/**
 * Creates a new, valid reflection object from a partial reflection object with
 * at least the `entryKey` property.  All other unprovided fields will be added to
 * the final object with default values.
 *
 * @param {ReflectionObject} fields             a partial reflection object
 * @param {boolean} isUpdate                    is this an update to a known, existing reflection object?
 * @returns {typeof LOWLIGHT_REFLECTION_OBJECT} the new reflection object
 */
export const newReflectionObject = (fields, isUpdate = false) =>
  setValidReflectionObjectFields(fields, isUpdate)

/**
 * Provided a reflection object with at least a single prop `entryKey` it properly
 * generates and assigns valid keys and values to the reflection object.  It
 * prioritizes reflection data saved on the `reflection` field of the associated
 * entry (parent entry), falls back to the provided value, or finally a default
 * value.
 *
 * @see functions `validateReflectionObjects` and `newReflectionObject`
 * @param {ReflectionObject} reflection   the reflection object's data
 * @param {boolean} prioritizeProvided    whether the parent entry's data should override the provided fields (use provided for updates to known, existing objects)
 */
export const setValidReflectionObjectFields = (
  reflection,
  prioritizeProvided = false
) => {
  let parentEntry, parentEntryReflection
  /** Define all the valid fields of a reflection object */
  const validKeys = REFLECTION_OBJECT_KEYS

  /** If a reflection object is missing an entryKey prop, remove it */
  if (!reflection.entryKey) {
    // TODO: Attempt a fallback to use `lowlightIndex` prop if existing and match it to corresponding
    // lowlight userEntry that is associated with this review and build new ref object with data that can be salvaged.
    reflection = null
    return reflection
  }

  /** Read reflection parent entry from cache and override review JSON data
   * values, if present */
  if (!prioritizeProvided) {
    try {
      parentEntry = cache.readPartialDocument({
        id: reflection.entryKey,
        fragment: gql`
          fragment ReflectionObjectParentEntry on UserEntry {
            reflection
          }
        `,
      })
      parentEntryReflection =
        parentEntry.reflection && parentEntry.refection !== "{}"
          ? JSON.parse(parentEntry.reflection)
          : {}
    } catch (err) {
      console.warn(
        "LOG: [setValidReflectionObjectFields] error while parsing parent entry reflection object"
      )
      console.log("LOG: [setValidReflectionObjectFields] parent entry data", {
        parentEntry,
        parentEntryReflection,
      })
      parentEntryReflection = {}
    }
  } else {
    parentEntryReflection = reflection
  }

  /** Array of all keys on the reflection object */
  const keys = Object.keys(reflection)

  /** Delete invalid keys, set valid key appropriate value */
  keys.forEach(key => {
    if (!validKeys.includes(key)) {
      delete reflection[key]
    } else {
      if (key === "updatedAt") reflection[key] = parentEntryReflection[key] || 0
      else reflection[key] = parentEntryReflection[key] || reflection[key] || ""
    }
  })

  /** Add any missing valid keys */
  validKeys.forEach(validKey => {
    if (!keys.includes(validKey)) {
      if (validKey === "updatedAt")
        reflection[validKey] =
          parentEntryReflection[validKey] || reflection[validKey] || 0
      else if (validKey === "__typename")
        reflection[validKey] =
          parentEntryReflection[validKey] ||
          reflection[validKey] ||
          "LowlightNote"
      else
        reflection[validKey] =
          parentEntryReflection[validKey] || reflection[validKey] || ""
    }
  })

  // debugger
  return reflection
}

/**
 * Validates the keys in a reflection object.
 * @param {ReflectionObject} reflectionObject   the reflection object
 * @param {string} type                         the type of reflection object
 */
export function isValidReflectionObject(reflectionObject, type = "lowlight") {
  const validKeys = REFLECTION_OBJECT_KEYS
  let isValid = true
  switch (type) {
    case "lowlight":
      /** Array of all keys on the reflection object */
      const keys = Object.keys(reflectionObject)
      /** If any of the keys are not valid, delete the key from the ref object */
      keys.forEach(key => {
        if (!validKeys.includes(key)) isValid = false
      })
      /** Add any missing valid keys, with an exception for `id` */
      validKeys.forEach(validKey => {
        if (!keys.includes(validKey) && validKey !== "id") isValid = false
      })
      break
  }
  return isValid
}

/**
 * It iterates through each reflection object item provided to it and sets valid
 * fields and their values onto the object, while removing any invalid fields from
 * the object, returning a perfectly valid reflection object with a properly
 * derived value depending on various review data edge cases from progressive versions
 * and features.
 *
 * @see function `setReviewData` executed when a review entry is clicked and loaded from journal
 * @param {ReviewDataObject} reviewData   the review object's data
 * @param {"lowlight"} type               the type of reflection objects provided
 */
export function validateReflectionObjects(reviewData, type = "lowlight") {
  if (!reviewData.id) return reviewData

  switch (type) {
    case "lowlight":
      let { lowlightsNotes: lowlightReflections } = reviewData
      // @ts-ignore
      lowlightReflections.forEach((reflection, index) => {
        reflection = setValidReflectionObjectFields(reflection)
      })
      /** Filter out any removed reflection objects from validation */
      lowlightReflections = lowlightReflections.filter(ref => ref !== null)
      break
    default:
      break
  }

  // debugger
  return reviewData
}

/**
 * Updates the reflection object on the review data in cache and the individual
 * entry associated with this reflection on the server, updating it's `reflection`
 * field.
 *
 * @param {ReflectionObject} data     the updated reflection object
 * @param {ReflectionObject} current  the current reflection object
 * @return {ReflectionObject}         the updated entry
 */
export const updateReflectionObjectEntry = (data, current) => {
  /** Update timestamp for logic to handle update conflicts, and ensure ,only the freshest
   * changes are saved to the database, by comparing timestamps before saving. */
  data.updatedAt = new Date().getTime()

  const isValid = isValidReflectionObject(data)

  if (!isValid) {
    errorer({
      title: "Whoopsala!",
      code: "reflection-update-invalid",
      ui: "modal",
      message:
        "There was a problem updating the data in this review as it contains invalid data.",
      internal:
        "`updateReflectionObject` found the provided reflection object is invalid.",
    })
    return current
  }

  updateReflectionParentEntry(data, "cache-and-network")

  return data
}

/**
 * Validates the review data object
 * @param {ReviewDataObject} reviewData   the reflection object's data
 * @param {string} label                  a source to use a label for caught errors
 */
export function isValidReviewDataObject(
  reviewData,
  label = "isValidReviewDataObject"
) {
  // if (typeof reviewData !== "object" || !reviewData.id) {
  if (typeof reviewData !== "object") {
    console.log(
      `LOG: [isValidReviewDataObject] -> reviewData (invalid)`,
      reviewData
    )
    throw Error(`Invalid review data provided to \`${label}\``)
  }

  const type = _deriveReviewType(reviewData)

  //TODO: Validate review data in isValideReviewDataObject
  const dataIsValid = true

  if (!dataIsValid) {
    console.log(
      `LOG: [isValidReviewDataObject] -> reviewData (invalid)`,
      reviewData
    )
    throw Error(
      `Invalid review data provided to type ${type} from \`${label}\``
    )
  }
  return dataIsValid
}

/**
 * Extracts the reflections found within the review data JSON and processes
 * each reflection object, updating the parent entry cache document.
 *
 * @see   `monthlyReview` and `yearlyReview` subscriptions
 * @param {ReviewDataObject} reviewData   the review object's data
 */
export function processReviewReflections(reviewData) {
  /** @type {string[]} */
  let updatedParentEntryIds
  // @ts-ignore
  const type = _deriveReviewType(reviewData)
  if (!isValidReviewDataObject(reviewData, "processReviewReflections"))
    _throwInvalidReviewData(reviewData, "processReviewReflections")

  /** Define which props on data contain reflection type objects */
  const reflectionProps = ["lowlightsNotes"]

  /** @type {ReflectionObject[]} */
  const reflectionObjects = Object.keys(reviewData).reduce((objects, prop) => {
    if (reflectionProps.includes(prop)) {
      if (reviewData.hasOwnProperty(prop))
        objects = [...objects, ...reviewData[prop]]
    }
    return objects
  }, [])

  switch (type) {
    case "monthly":
    case "yearly":
      updatedParentEntryIds = reflectionObjects.reduce((ids, object) => {
        try {
          updateReflectionParentEntry(object, "cache-and-network")
          ids.push(object.entryKey)
          return ids
        } catch (err) {
          console.warn(
            "LOG: [processReviewReflections] update to reflection parent entry cache document failed."
          )
          console.error(err)
        }
      }, [])
      break
    default:
      break
  }

  // debugger
  return updatedParentEntryIds
}

/**
 * Updates the entry associated with the provided reflection object in Apollo cache or on the server
 * based on the provided target.
 *
 * @see   `monthlyReview` and `yearlyReview` subscriptions
 * @param {ReflectionObject} reflectionObject   the reflection object's data
 * @param {"cache"|"cache-and-network"} target  where to update entry
 */
export function updateReflectionParentEntry(
  reflectionObject,
  target = "cache"
) {
  if (!isValidReflectionObject(reflectionObject)) {
    console.log(
      `LOG: [updateReflectionParentEntry] -> reflectionObject (invalid)`,
      reflectionObject
    )
    throw Error(
      "Invalid reflection object provided to `updateReflectionParentEntry`"
    )
  }

  function updateCache(reflectionObject) {
    /** Attempt to update parent entry item in Apollo cache */
    try {
      /** Create review parent entry fragment data */
      const update = {
        reflection: JSON.stringify(reflectionObject),
        __typename: "UserEntry",
      }
      /** Write to cache */
      cache.writePartialDocument(
        {
          id: reflectionObject.entryKey,
          fragment: gql`
            fragment ParentEntryReflectionObject on UserEntry {
              reflection
            }
          `,
          data: update,
        },
        {
          label: "updateReflectionParentEntry",
          next: target === "cache-and-network" ? updateNetwork : null,
          rollbackDocType: "UserEntry",
          skipDiffCheck: true,
        }
      )
    } catch (err) {
      debugger //_
      console.error(err)
      throw Error("Cache update failed from `updateReflectionParentEntry`")
    }
  }

  /**
   *  Updates the entry on the network as a callback to the cache update
   *
   * @param {import("apollo-cache").DataProxy.WriteFragmentOptions<any, import("apollo-boost").OperationVariables>} options required options for operation
   * @param {() => {}} onError a function provided with the correct configuration
   * to roll back cache with `writeFragment` in case of network error.
   */
  function updateNetwork(options, onError) {
    const update = options.data
    update.key = options.id
    /** Update this entry on the server */
    network.mutate(
      {
        mutation: UPDATE_ENTRY,
        variables: update,
      },
      { label: "updateReflectionParentEntry", next: null, error: onError }
    )
  }

  switch (target) {
    case "cache":
    case "cache-and-network":
      updateCache(reflectionObject)
      break
    default:
      break
  }
}

/**
 * Updates the entry associated with the provided review data in Apollo cache or on the server
 * based on the provided target.
 *
 * @see   `monthlyReview` and `yearlyReview` subscriptions
 * @param {ReviewDataObject} reviewData         review data object
 * @param {"cache"|"cache-and-network"} target  where to update entry
 * @example
 * ```
 *  try {
 *      updateReviewParentEntry(reviewData)
 *  } catch (err) {
 *    //handle error updating cache
 *  }
 * ```
 */
export function updateReviewParentEntry(reviewData, target = "cache") {
  if (!isValidReviewDataObject(reviewData, "updateReviewParentEntry"))
    _throwInvalidReviewData(reviewData, "updateReviewParentEntry")

  let startReviewData = null
  const type = _deriveReviewType(reviewData)

  /** `id` is null and `month` and/or `year` is set to the selected values, from the
   * Guides Start route selector at /a/guides. Otherwise `reviewData` is fresh based
   * on `YEARLY/MONTHLY_REVIEW_DATA` */
  if (!reviewData.id) {
    /** Create a new parent entry data structure */
    const newReviewParentData = {
      date:
        type === "yearly"
          ? moment(
              getNextDay(dateLastDayOfMonth("december", reviewData.year))
            ).toISOString(true)
          : moment(
              getNextDay(dateLastDayOfMonth(reviewData.month, reviewData.year))
            ).toISOString(true),
      image: "",
      month: type === "yearly" ? "january" : reviewData.month,
      pinned: false,
      platform: "web",
      review: JSON.stringify(reviewData),
      tags: "",
      text: "",
      type: `${type} review`,
      year: reviewData.year,
      reflection: "",
      saved: moment().toISOString(true),
      __typename: "UserEntry",
    }

    // debugger

    /** Create a new sorted UserEntry document in cache */
    const { id: createdId } = cache.addSortedDocument({
      fragment: USER_ENTRY_UPDATE(),
      data: newReviewParentData,
    })

    if (createdId) {
      /** Clone immutable `reviewData` object and add the created ID onto it. */
      startReviewData = cloneDeep(reviewData)
      startReviewData.id = createdId
      startReviewData.date = newReviewParentData.date

      // debugger

      // 🦄 uncomment this
      /** Trigger review started Klaviyo event */
      if (type === "yearly") setYearlyReviewStarted(startReviewData.year)
      if (type === "monthly")
        setMonthlyReviewStarted(startReviewData.month, startReviewData.year)

      /** Write this update to review global state object, which will immediately retrigger
       * the subscription, which triggers this function. Execution will then continue with
       * `updateCache` below and then a subsequent network call to sync with the server */
      writeReviewData(startReviewData)
    } else {
      alert(
        "There was an issue while initializing this review.  We'll attempt to refresh the application now, and then try again to create this review."
      )
      // window.location.assign("/a/account")
      debugger //_
      return
    }
  }

  /**
   * Update the cache
   * @param {ReviewDataObject} reviewData review data
   */
  function updateCache(reviewData) {
    /** Attempt to update parent entry item in Apollo cache */
    try {
      /** Create review parent entry fragment data */
      let update = {
        review: JSON.stringify(reviewData),
        type: _deriveReviewType(reviewData) + " review",
        __typename: "UserEntry",
      }

      if (reviewData.date) update.date = reviewData.date
      if (reviewData.month) update.month = reviewData.month
      if (reviewData.year) update.year = reviewData.year

      const fields = `${Object.keys(update).reduce(
        (fields, acc) => (acc === "review" ? fields : `${fields}\n${acc}`),
        "review"
      )}`

      // debugger
      /** Write to cache */
      cache.writePartialDocument(
        {
          id: reviewData.id,
          /** Create a list of fields based on the properties on `update`, acc starts with "review", a constant field */
          fragment: gql`
            fragment ParentEntryReview on UserEntry {
              ${fields}
            }
          `,
          data: update,
        },
        // @ts-ignore
        {
          label: "updateReviewParentEntry",
          next: target === "cache-and-network" ? updateNetwork : null,
          rollbackDocType: "UserEntry",
        }
      )
    } catch (err) {
      console.error(err)
      throw Error("Cache update failed from `updateReviewParentEntry`")
    }
  }

  /**
   *  Updates the entry on the network as a callback to the cache update
   *
   * @param {import("apollo-cache").DataProxy.WriteFragmentOptions<any, import("apollo-boost").OperationVariables>} options required options for operation
   * @param {() => {}} onError a function provided with the correct configuration
   * to roll back cache with `writeFragment` in case of network error.
   */
  const updateNetwork = (options, onError) => {
    const update = options.data
    update.key = options.id
    /** Update this entry on the server */
    network.mutate(
      {
        mutation: UPDATE_ENTRY,
        variables: update,
      },
      {
        label: "updateParentReviewEntry",
        next: () => {
          // debugger
          if (startReviewData) {
            if (type === "yearly")
              navigate(`/a/compose/yearly/${startReviewData.year}/1`)

            if (type === "monthly")
              navigate(
                `/a/compose/monthly/${startReviewData.month}/${startReviewData.year}/1`
              )
          }
        },
        error: onError,
      }
    )
  }

  switch (target) {
    case "cache":
    case "cache-and-network":
      updateCache(startReviewData || reviewData)
      break
    default:
      break
  }
}

export function queryReviewData(type = "monthly") {
  let queryResult, data
  switch (type) {
    case "monthly":
      queryResult = cache.readQuery({ query: MONTHLY_REVIEW })?.monthlyReview
      // Is a Reactive Variable
      data = queryResult()
      break
    case "yearly":
      queryResult = cache.readQuery({ query: YEARLY_REVIEW })?.yearlyReview
      // Is not a reactive variable
      data = queryResult
      break
  }
  return data
}

export function initReviewData(reviewData) {
  let initedReview = cloneDeep(reviewData)
  initedReview = appendMissingReflectionObjects(initedReview)
  return intitedReview
}

export function resetReviewData(year = null, month = null) {
  const monthlyReviewData = MONTHLY_REVIEW_DATA
  const yearlyReviewData = YEARLY_REVIEW_DATA
  const type = month && year ? "monthly" : "yearly"

  if (!year) {
    alert(
      "There's a problem with your selection.  Please refresh the page and try staring this guide again."
    )
    return
  }

  const monthly = () =>
    setApolloState("monthlyReview", {
      ...monthlyReviewData,
      month,
      year,
    })

  const yearly = () =>
    setApolloState("yearlyReview", {
      ...yearlyReviewData,
      year,
    })

  switch (type) {
    case "monthly":
      monthly()
      break
    case "yearly":
      yearly()
      break
    default:
      monthly()
      yearly()
  }

  return { monthly: monthly().monthlyReview, yearly: yearly().yearlyReview }
}

/**
 * Write the review data to cache.
 *
 * @param {{[key: string]: any}} data   the update object
 * @param {"monthly"|"yearly"} type     the type of review
 */
export function writeReviewData(data) {
  // @ts-ignore
  const type = _deriveReviewType(data)
  let query =
    type === "monthly"
      ? MONTHLY_REVIEW
      : type === "yearly"
      ? YEARLY_REVIEW
      : null
  if (!query) {
    throw Error(
      `Invalid query from type \`${type}\` provided to \`writeReviewData\`.`
    )
  }
  const property = `${type}Review`
  let queryResult = cache.readQuery({ query }, "writeReviewData")
  queryResult =
    typeof queryResult === "function" ? queryResult() : queryResult[property]
  const update = {
    ...queryResult,
    ...data,
    saved: moment().toISOString(true),
  }
  debugger
  setApolloState(property, update)
  // window.localStorage.setItem(`_${property}`, JSON.stringify(data))
  return update[property]
}

export function handleLocalStoreVersion(
  typeOrReviewdata = null,
  action = "retrieve"
) {
  const type =
    typeof typeOrReviewdata === "object"
      ? _deriveReviewType(typeOrReviewdata)
      : typeOrReviewdata
  const value = {}
  switch (action) {
    case "retrieve":
      const localStoreVersion = window.localStorage.getItem(`_${type}Review`)
      try {
        const parsed = JSON.parse(localStoreVersion)
        value.data = parsed
      } catch (err) {
        console.warn(
          "LOG: [compareSubscriptionToLocalStorage] failed to parse local storage version"
        )
      }
      break
    case "delete":
      window.localStorage.removeItem(`_${type}Review`)
      value.deleted = true
      break
  }
  return value
}

export function compareSubscriptionToLocalStorage(data) {
  let result = { change: false, currentVersion: data, localStoreVersion: null }
  const type = _deriveReviewType(data)
  const localStoreVersion = window.localStorage.getItem(`_${type}Review`)
  try {
    const parsed = JSON.parse(localStoreVersion)
    if (parsed?.id !== data?.id) {
      result.change = true
      result.localStoreVersion = parsed
    }
  } catch (err) {
    console.warn(
      "LOG: [compareSubscriptionToLocalStorage] failed to parse local storage version"
    )
  }
  return result
}

export function handleReviewQuestionField(
  reviewData,
  sectionKey,
  sectionItemIndex,
  subsectionKey,
  value
) {
  // let __reviewData = cloneDeep(reviewData)
  let __reviewData = reviewData
  let type = _deriveReviewType(__reviewData)
  let _reviewData = cache.readQuery({
    query: type === "monthly" ? MONTHLY_REVIEW : YEARLY_REVIEW,
  })[`${type}Review`]

  if (!_reviewData || !_reviewData.id) {
    console.log({ reviewData, _reviewData })
    window.TrackJS &&
      window.TrackJS.track(
        `handleReviewQuestionField reviewData derivision failure`
      )
    alert(
      "There was an unexpected error while saving this review's data.  Please try logging out or refreshing the page.  If the problem persists, contact support."
    )
    return
  }

  _reviewData = cloneDeep(_reviewData)

  switch (sectionKey) {
    // case "highlightsNote":
    //   _reviewData[sectionKey] = value
    //   break
    case "categoryQuestions":
      _reviewData[sectionKey][subsectionKey][sectionItemIndex].response = value
      break
    case "rankings":
      _reviewData[sectionKey][subsectionKey][Object.keys(value)[0]] =
        value[Object.keys(value)[0]]
      break
    default:
      if (Number.isInteger(sectionItemIndex))
        _reviewData[sectionKey][sectionItemIndex].response = value
      else _reviewData[sectionKey] = value
      break
  }

  writeReviewData(_reviewData)
}

export function verifyCacheAndNetworkSync(reviewData) {
  let parentEntryReviewData,
    check = {},
    isDiff = true // diff until proven identical
  const { id } = reviewData

  try {
    /** Get the review data from the review's parent entry */
    let { review } = cache.readPartialDocument({
      id,
      fragment: gql`
        fragment CacheNetworkSyncReviewParent on UserEntry {
          review
        }
      `,
    })
    parentEntryReviewData = JSON.parse(review)
    check = diffCheckCacheWrite(parentEntryReviewData, reviewData)
    isDiff = Object.keys(check).length > 0
  } catch (err) {
    console.warn(
      "LOG: [verifyCacheAndNetworkSync] produced an error: ",
      err.message
    )
    console.error(err)
  }

  if (isDiff) {
    errorer({
      title: "Warning: Data Sync Issue",
      code: "data-sync-issue",
      ui: "modal",
      message:
        "You have unsaved data in this review that isn't synced with the server, therefore if you navigate away now, you might lose some of your work.  Try making a change somewhere to initiate an autosave.  If this message reappears and persists, do not close this page without saving any work you've done locally!  Please check your internet connection, or try again in a few moments.",
      internal: "Unsynced review data fields: " + Object.keys(check).join(", "),
    })
    console.error("Unsynced review data error")
    console.log("LOG: [verifyCacheAndNetworkSync] unsynced data", {
      isDiff: `${isDiff}`,
      parentEntryReviewData: { ...parentEntryReviewData },
      reviewData: { ...reviewData },
      diffCheck: { ...check },
    })
  }

  return isDiff
}

/**
 * 
 * 
 * 
 * 
 * 
 * 
 * 
 * 












 
 */
